// WindowMetalPanelsSection.jsx
// Windows Metal Panel Types - Same structure as DoorMetalPanelsSection

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/NumericField';
import { getNumericFieldError } from '@/utils/numericValidation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';

export const WindowMetalPanelsSection = ({
  // Data
  windowMetalPanelTypes,
  selectedWindowMetalPanelTypeIds,
  canEditCSIItems,
  canManageProject,
  operationLoading,
  // Dialog state
  showWindowMetalPanelTypeDialog,
  editingWindowMetalPanelType,
  newWindowMetalPanelType,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onFormChange,
  onFormSubmit,
  onEdit,
  onDelete,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const thicknessRules = { required: true, min: 0, allowFraction: true, label: 'Thickness' };
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (getNumericFieldError(newWindowMetalPanelType.thickness, thicknessRules)) {
      e.preventDefault();
      return;
    }
    onFormSubmit(e);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Metal Panel Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && windowMetalPanelTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={onSelectAll}
              >
                {selectedWindowMetalPanelTypeIds.length === windowMetalPanelTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={onConfirmBulkDelete}
                disabled={selectedWindowMetalPanelTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedWindowMetalPanelTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showWindowMetalPanelTypeDialog} onOpenChange={(open) => { setSubmitAttempted(false); onDialogOpenChange(open); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-window-metal-panel-type-btn" disabled={operationLoading}>
                  {operationLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Metal Panel Type
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingWindowMetalPanelType ? 'Edit Metal Panel Type' : 'Create Metal Panel Type'}</DialogTitle>
                  <DialogDescription>Define metal panel specifications for windows</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type (e.g., MT1, MT2) *</Label>
                      <Input 
                        value={newWindowMetalPanelType.name} 
                        onChange={(e) => onFormChange({...newWindowMetalPanelType, name: e.target.value})} 
                        placeholder="e.g., MT1, MT2"
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manufacturer *</Label>
                      <Input 
                        value={newWindowMetalPanelType.manufacturer || ''} 
                        onChange={(e) => onFormChange({...newWindowMetalPanelType, manufacturer: e.target.value})} 
                        placeholder="e.g., Kawneer, YKK"
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model *</Label>
                      <Input 
                        value={newWindowMetalPanelType.model || ''} 
                        onChange={(e) => onFormChange({...newWindowMetalPanelType, model: e.target.value})} 
                        placeholder="e.g., Series 500"
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Thickness (in) *</Label>
                      <NumericField
                        value={newWindowMetalPanelType.thickness}
                        onChange={(v) => onFormChange({...newWindowMetalPanelType, thickness: v})}
                        rules={thicknessRules}
                        showError={submitAttempted}
                        placeholder="e.g., 0.125"
                        required 
                        data-testid="window-metal-thickness-input"
                        errorTestId="window-metal-thickness-error"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Color Code (HEX/RAL)</Label>
                      <Input 
                        value={newWindowMetalPanelType.colorCode || ''} 
                        onChange={(e) => onFormChange({...newWindowMetalPanelType, colorCode: e.target.value})} 
                        placeholder="#FFFFFF or RAL 9016" 
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onDialogOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={operationLoading}>
                      {operationLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {editingWindowMetalPanelType ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingWindowMetalPanelType ? 'Update Metal Type' : 'Create Metal Type'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {windowMetalPanelTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No metal panel types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedWindowMetalPanelTypeIds.length === windowMetalPanelTypes.length && windowMetalPanelTypes.length > 0}
                      onCheckedChange={onSelectAll}
                    />
                  </th>
                )}
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Thickness</th>
                <th className="w-28 px-3 py-3 text-left font-semibold text-slate-700">Color Code</th>
                {canEditCSIItems && (
                  <th className="w-20 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {windowMetalPanelTypes.map((metal, idx) => (
                <tr 
                  key={metal.id}
                  data-testid={`window-metal-panel-type-${metal.id}`}
                  className={`border-b hover:bg-slate-50 transition-colors ${
                    selectedWindowMetalPanelTypeIds.includes(metal.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                  }`}
                >
                  {canEditCSIItems && (
                    <td className="w-12 px-3 py-2">
                      <Checkbox
                        checked={selectedWindowMetalPanelTypeIds.includes(metal.id)}
                        onCheckedChange={(checked) => onToggleSelection(metal.id, { target: { checked } })}
                      />
                    </td>
                  )}
                  <td className="w-20 px-3 py-2 font-medium text-slate-900">{metal.name}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.manufacturer || '-'}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.model || '-'}</td>
                  <td className="w-24 px-3 py-2 text-slate-600">{metal.thickness ? `${metal.thickness}"` : '-'}</td>
                  <td className="w-28 px-3 py-2 text-slate-600">{metal.colorCode || '-'}</td>
                  {canEditCSIItems && (
                    <td className="w-20 px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(metal)}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => onDelete(metal.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WindowMetalPanelsSection;
